import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Button, DatePicker, Input, Message, Panel, Toggle } from 'rsuite';
import { ConfirmModal } from '@nsi-advisory-services/components';
import { showToast } from '../utils/toast';
import LoadingSpinner from '../components/LoadingSpinner';
import { useAuth } from '../contexts/AuthContext';
import { useCompanyDirectory, useEmployee, useResume, useSaveResume } from '../hooks/useQueries';
import './ResumePage.css';

// Time the exit animation a touch longer than the CSS so the collapse finishes
// before the row is unmounted.
const REMOVE_MS = 300;

let uidCounter = 0;
const makeKey = () => {
  uidCounter += 1;
  return `r${uidCounter}`;
};

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const toDateOnly = (value) => {
  if (!value) return null;
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const parseDateOnlyLocal = (value) => {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  const s = String(value);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) {
    const parsed = new Date(s);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(d.getTime()) ? null : d;
};

const formatMonthYear = (value) => {
  const d = parseDateOnlyLocal(value);
  if (!d) return '';
  return `${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
};

const formatRange = (startVal, endVal, openLabel) => {
  const start = formatMonthYear(startVal);
  const end = endVal ? formatMonthYear(endVal) : openLabel;
  if (!start && !endVal) return openLabel;
  if (!start) return end;
  return `${start} – ${end}`;
};

// Stable string representation of an item's saved content (no keys/UI flags),
// used to detect unsaved changes against the last-saved baseline.
const SERIALIZERS = {
  experiences: (exp) => JSON.stringify({
    company: exp.company || '',
    start_date: exp.start_date || null,
    end_date: exp.end_date || null,
    functions: (exp.functions || []).map((fn) => fn.description || ''),
  }),
  competencies: (comp) => JSON.stringify({
    name: comp.name || '',
    description: comp.description || '',
  }),
  certifications: (cert) => JSON.stringify({
    name: cert.name || '',
    governing_organization: cert.governing_organization || '',
    start_date: cert.start_date || null,
    end_date: cert.end_date || null,
  }),
};

const computeBaseline = (st) => {
  const out = { experiences: {}, competencies: {}, certifications: {} };
  Object.keys(out).forEach((listName) => {
    st[listName].forEach((item) => {
      out[listName][item.key] = SERIALIZERS[listName](item);
    });
  });
  return out;
};


// Map the API resume shape into local editor state (adding stable keys).
// Loaded/saved items start collapsed (editing: false).
const hydrateState = (data) => ({
  experiences: (data?.experiences || []).map((exp) => ({
    key: makeKey(),
    company: exp.company || '',
    start_date: exp.start_date || null,
    end_date: exp.end_date || null,
    functions: (exp.functions || []).map((fn) => ({
      key: makeKey(),
      description: typeof fn === 'string' ? fn : fn.description || '',
    })),
    editing: false,
    removing: false,
  })),
  competencies: (data?.competencies || []).map((comp) => ({
    key: makeKey(),
    name: comp.name || '',
    description: comp.description || '',
    editing: false,
    removing: false,
  })),
  certifications: (data?.certifications || []).map((cert) => ({
    key: makeKey(),
    name: cert.name || '',
    governing_organization: cert.governing_organization || '',
    start_date: cert.start_date || null,
    end_date: cert.end_date || null,
    editing: false,
    removing: false,
  })),
});

export default function ResumePage() {
  const auth = useAuth();
  const { id: routeEmployeeId } = useParams();
  const isManagingOther = Boolean(routeEmployeeId);
  const employeeId = isManagingOther ? routeEmployeeId : 'me';

  const canEdit = isManagingOther
    ? (auth.isAdmin() || auth.hasPermission('manage_all_employees') || auth.hasPermission('manage_coached_employees') || auth.hasPermission('manage_resumes'))
    : true;

  const employeeQuery = useEmployee(routeEmployeeId, { enabled: isManagingOther });
  // Fallback name source: resume-only managers can't read the full employee
  // record, but the company directory is available to everyone.
  const directoryQuery = useCompanyDirectory({ enabled: isManagingOther });
  const resumeQuery = useResume(employeeId, { enabled: true });
  const saveResume = useSaveResume();

  const [state, setState] = useState({ experiences: [], competencies: [], certifications: [] });
  const [savingKey, setSavingKey] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);
  const baselineRef = useRef({ experiences: {}, competencies: {}, certifications: {} });
  // Which employee's resume the local editor state was hydrated from. Used to
  // hydrate only on first load (or when navigating to another resume), not on
  // every refetch — save/delete invalidate the query, and re-hydrating on the
  // resulting refetch would collapse all items, regenerate keys, and clobber
  // edits in progress in other sections.
  const hydratedForRef = useRef(null);

  const pushSavedToast = () => {
    showToast('success', 'Resume saved.');
  };

  useEffect(() => {
    if (resumeQuery.data && hydratedForRef.current !== employeeId) {
      const hydrated = hydrateState(resumeQuery.data);
      setState(hydrated);
      baselineRef.current = computeBaseline(hydrated);
      hydratedForRef.current = employeeId;
    }
  }, [resumeQuery.data, employeeId]);

  // Dirty tracking against the last-saved baseline.
  const isItemDirty = (listName, item) => {
    const base = baselineRef.current[listName][item.key];
    return base === undefined || base !== SERIALIZERS[listName](item);
  };

  const isListDirty = (listName) => {
    const base = baselineRef.current[listName];
    const currentKeys = new Set(state[listName].map((item) => item.key));
    // An item that existed in the baseline but is gone now = a removal.
    if (Object.keys(base).some((key) => !currentKeys.has(key))) return true;
    return state[listName].some((item) => isItemDirty(listName, item));
  };

  const isDirty = isListDirty('experiences') || isListDirty('competencies') || isListDirty('certifications');

  const headerName = useMemo(() => {
    if (!isManagingOther) return 'My Resume';
    const emp = employeeQuery.data
      || (directoryQuery.data || []).find((e) => String(e.id) === String(routeEmployeeId));
    const name = `${emp?.first_name || ''} ${emp?.last_name || ''}`.trim();
    return name ? `${name} — Resume` : 'Resume';
  }, [isManagingOther, employeeQuery.data, directoryQuery.data, routeEmployeeId]);

  // Generic helpers for the three top-level lists.
  const updateList = (listName, key, patch) => {
    setState((s) => ({
      ...s,
      [listName]: s[listName].map((item) => (item.key === key ? { ...item, ...patch } : item)),
    }));
  };

  // Ask before removing a top-level item.
  const NOUNS = { experiences: 'experience', competencies: 'competency', certifications: 'certification' };

  const requestDelete = (listName, item) => {
    setPendingDelete({
      listName,
      key: item.key,
      noun: NOUNS[listName],
      name: (listName === 'experiences' ? item.company : item.name) || '',
    });
  };

  // Remove the item (with the collapse animation) and persist immediately.
  const confirmDelete = async () => {
    if (!pendingDelete) return;
    const { listName, key } = pendingDelete;
    setPendingDelete(null);
    setSavingKey(key);

    updateList(listName, key, { removing: true });
    const nextState = { ...state, [listName]: state[listName].filter((item) => item.key !== key) };
    const animation = new Promise((resolve) => setTimeout(resolve, REMOVE_MS));

    try {
      await saveResume.mutateAsync(buildPayload(nextState));
      await animation;
      setState((s) => ({ ...s, [listName]: s[listName].filter((item) => item.key !== key) }));
      baselineRef.current = computeBaseline(nextState);
      pushSavedToast();
    } catch (err) {
      // Restore the row if the save failed.
      updateList(listName, key, { removing: false });
    } finally {
      setSavingKey(null);
    }
  };

  const cancelDelete = () => setPendingDelete(null);

  // ----- Experience -----
  const addExperience = () => {
    setState((s) => ({
      ...s,
      experiences: [...s.experiences, { key: makeKey(), company: '', start_date: null, end_date: null, functions: [], editing: true, removing: false }],
    }));
  };

  const addFunction = (expKey) => {
    setState((s) => ({
      ...s,
      experiences: s.experiences.map((exp) =>
        exp.key === expKey
          ? { ...exp, functions: [...exp.functions, { key: makeKey(), description: '', removing: false }] }
          : exp
      ),
    }));
  };

  const updateFunction = (expKey, fnKey, description) => {
    setState((s) => ({
      ...s,
      experiences: s.experiences.map((exp) =>
        exp.key === expKey
          ? { ...exp, functions: exp.functions.map((fn) => (fn.key === fnKey ? { ...fn, description } : fn)) }
          : exp
      ),
    }));
  };

  const removeFunction = (expKey, fnKey) => {
    setState((s) => ({
      ...s,
      experiences: s.experiences.map((exp) =>
        exp.key === expKey
          ? { ...exp, functions: exp.functions.map((fn) => (fn.key === fnKey ? { ...fn, removing: true } : fn)) }
          : exp
      ),
    }));
    setTimeout(() => {
      setState((s) => ({
        ...s,
        experiences: s.experiences.map((exp) =>
          exp.key === expKey ? { ...exp, functions: exp.functions.filter((fn) => fn.key !== fnKey) } : exp
        ),
      }));
    }, REMOVE_MS);
  };

  // ----- Competency -----
  const addCompetency = () => {
    setState((s) => ({
      ...s,
      competencies: [...s.competencies, { key: makeKey(), name: '', description: '', editing: true, removing: false }],
    }));
  };

  // ----- Certification -----
  const addCertification = () => {
    setState((s) => ({
      ...s,
      certifications: [...s.certifications, { key: makeKey(), name: '', governing_organization: '', start_date: null, end_date: null, editing: true, removing: false }],
    }));
  };

  const buildPayload = (st = state) => ({
    employeeId,
    experiences: st.experiences.map((exp) => ({
      company: exp.company,
      start_date: exp.start_date,
      end_date: exp.end_date,
      functions: exp.functions.map((fn) => fn.description),
    })),
    competencies: st.competencies.map((comp) => ({
      name: comp.name,
      description: comp.description,
    })),
    certifications: st.certifications.map((cert) => ({
      name: cert.name,
      governing_organization: cert.governing_organization,
      start_date: cert.start_date,
      end_date: cert.end_date,
    })),
  });

  // Save the whole resume and re-collapse everything.
  const handleSave = async () => {
    const result = await saveResume.mutateAsync(buildPayload());
    const hydrated = hydrateState(result);
    setState(hydrated);
    baselineRef.current = computeBaseline(hydrated);
    pushSavedToast();
  };

  // Save the whole resume (full replace) but collapse only this item.
  const saveItem = async (listName, key) => {
    setSavingKey(key);
    try {
      await saveResume.mutateAsync(buildPayload());
      // The full resume was persisted, so the whole current state is now the baseline.
      baselineRef.current = computeBaseline(state);
      updateList(listName, key, { editing: false });
      pushSavedToast();
    } finally {
      setSavingKey(null);
    }
  };

  if (resumeQuery.isLoading || (isManagingOther && employeeQuery.isLoading)) {
    return <LoadingSpinner message="Loading resume..." />;
  }

  if (resumeQuery.error) {
    return (
      <Message type="error" header="Error" showIcon>
        {String(resumeQuery.error?.message || resumeQuery.error)}
      </Message>
    );
  }

  const renderActions = (item, listName) => {
    if (!canEdit) return null;
    return (
      <div className="resume-summary-actions">
        {!item.editing ? (
          <Button appearance="subtle" size="sm" onClick={() => updateList(listName, item.key, { editing: true })}>
            <i className="bi bi-pencil me-2" />
            Edit
          </Button>
        ) : null}
        <Button appearance="subtle" color="red" size="sm" onClick={() => requestDelete(listName, item)} title="Remove">
          <i className="bi bi-trash" />
        </Button>
      </div>
    );
  };

  return (
    <div className="resume-page d-flex flex-column gap-3">
      <div className="resume-page-header">
        <h4 className="m-0">{headerName}</h4>
        {canEdit ? (
          <Button
            appearance="primary"
            onClick={handleSave}
            loading={saveResume.isPending && savingKey === null}
            disabled={!isDirty || saveResume.isPending}
          >
            <i className="bi bi-floppy me-2" />
            Save Resume
          </Button>
        ) : null}
      </div>

      {saveResume.error ? (
        <Message type="error" header="Error" showIcon>
          {String(saveResume.error?.message || saveResume.error)}
        </Message>
      ) : null}

      {!canEdit ? (
        <Message type="info" showIcon>
          You have read-only access to this resume.
        </Message>
      ) : null}

      {/* Experience */}
      <Panel bordered header={<span className="fw-semibold">Experience</span>}>
        {state.experiences.length === 0 ? (
          <div className="text-muted">No experience entries.</div>
        ) : (
          <div className="resume-grid">
            {state.experiences.map((exp) => (
              <div key={exp.key} className={`resume-entry ${exp.editing ? 'resume-entry--editing' : ''} ${exp.removing ? 'removing' : ''}`}>
                {exp.editing ? (
                  <>
                    <div className="resume-entry-grid">
                      <div className="resume-field resume-field--grow">
                        <label className="resume-label">Company</label>
                        <Input
                          value={exp.company}
                          placeholder="Company name"
                          onChange={(v) => updateList('experiences', exp.key, { company: v })}
                        />
                      </div>
                      <div className="resume-field">
                        <label className="resume-label">Start Date</label>
                        <DatePicker
                          oneTap
                          block
                          format="MM/yyyy"
                          value={parseDateOnlyLocal(exp.start_date)}
                          onChange={(d) => updateList('experiences', exp.key, { start_date: toDateOnly(d) })}
                        />
                      </div>
                      <div className="resume-field">
                        <label className="resume-label">End Date</label>
                        <DatePicker
                          oneTap
                          block
                          format="MM/yyyy"
                          disabled={!exp.end_date}
                          value={parseDateOnlyLocal(exp.end_date)}
                          placeholder={exp.end_date ? undefined : 'Present'}
                          onChange={(d) => updateList('experiences', exp.key, { end_date: toDateOnly(d) })}
                        />
                        <label className="resume-current-toggle">
                          <Toggle
                            size="sm"
                            checked={!exp.end_date}
                            onChange={(checked) => updateList('experiences', exp.key, { end_date: checked ? null : toDateOnly(new Date()) })}
                          />
                          <span>Current</span>
                        </label>
                      </div>
                    </div>

                    <div className="resume-functions">
                      <div className="resume-functions-header">
                        <span className="resume-label">Functions Performed</span>
                      </div>
                      {exp.functions.length === 0 ? (
                        <div className="text-muted resume-functions-empty">No functions listed.</div>
                      ) : (
                        <div className="d-flex flex-column gap-2">
                          {exp.functions.map((fn) => (
                            <div key={fn.key} className={`resume-function-row ${fn.removing ? 'removing' : ''}`}>
                              <Input
                                value={fn.description}
                                placeholder="Describe a function performed"
                                onChange={(v) => updateFunction(exp.key, fn.key, v)}
                              />
                              <Button
                                appearance="subtle"
                                color="red"
                                size="sm"
                                onClick={() => removeFunction(exp.key, fn.key)}
                                title="Remove function"
                              >
                                <i className="bi bi-x-lg" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                      <div className="resume-add-row resume-add-row--inner">
                        <Button appearance="link" size="sm" onClick={() => addFunction(exp.key)}>
                          <i className="bi bi-plus-lg me-1" />
                          Add Function
                        </Button>
                      </div>
                    </div>

                    <div className="resume-entry-actions">
                      <Button
                        appearance="primary"
                        size="sm"
                        onClick={() => saveItem('experiences', exp.key)}
                        loading={savingKey === exp.key && saveResume.isPending}
                        disabled={!isItemDirty('experiences', exp) || saveResume.isPending}
                      >
                        <i className="bi bi-floppy me-2" />
                        Save
                      </Button>
                      <Button appearance="subtle" color="red" size="sm" onClick={() => requestDelete('experiences', exp)}>
                        <i className="bi bi-trash me-2" />
                        Remove Experience
                      </Button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="resume-summary">
                      <div className="resume-summary-main">
                        <div className="resume-summary-title">{exp.company || 'Untitled experience'}</div>
                        <div className="resume-summary-sub">{formatRange(exp.start_date, exp.end_date, 'Present')}</div>
                      </div>
                      {renderActions(exp, 'experiences')}
                    </div>
                    {exp.functions.length > 0 ? (
                      <ul className="resume-summary-functions">
                        {exp.functions.map((fn) => (
                          <li key={fn.key}>{fn.description}</li>
                        ))}
                      </ul>
                    ) : null}
                  </>
                )}
              </div>
            ))}
          </div>
        )}

        {canEdit ? (
          <div className="resume-add-row">
            <Button appearance="ghost" onClick={addExperience}>
              <i className="bi bi-plus-lg me-2" />
              Add Experience
            </Button>
          </div>
        ) : null}
      </Panel>

      {/* Competencies */}
      <Panel bordered header={<span className="fw-semibold">Competencies</span>}>
        {state.competencies.length === 0 ? (
          <div className="text-muted">No competencies.</div>
        ) : (
          <div className="resume-grid">
            {state.competencies.map((comp) => (
              <div key={comp.key} className={`resume-entry ${comp.editing ? 'resume-entry--editing' : ''} ${comp.removing ? 'removing' : ''}`}>
                {comp.editing ? (
                  <>
                    <div className="resume-field">
                      <label className="resume-label">Name</label>
                      <Input
                        value={comp.name}
                        placeholder="Competency name"
                        onChange={(v) => updateList('competencies', comp.key, { name: v })}
                      />
                    </div>
                    <div className="resume-field">
                      <label className="resume-label">Description</label>
                      <Input
                        as="textarea"
                        rows={2}
                        value={comp.description}
                        placeholder="Describe this competency"
                        onChange={(v) => updateList('competencies', comp.key, { description: v })}
                      />
                    </div>
                    <div className="resume-entry-actions">
                      <Button
                        appearance="primary"
                        size="sm"
                        onClick={() => saveItem('competencies', comp.key)}
                        loading={savingKey === comp.key && saveResume.isPending}
                        disabled={!isItemDirty('competencies', comp) || saveResume.isPending}
                      >
                        <i className="bi bi-floppy me-2" />
                        Save
                      </Button>
                      <Button appearance="subtle" color="red" size="sm" onClick={() => requestDelete('competencies', comp)}>
                        <i className="bi bi-trash me-2" />
                        Remove
                      </Button>
                    </div>
                  </>
                ) : (
                  <div className="resume-summary">
                    <div className="resume-summary-main">
                      <div className="resume-summary-title">{comp.name || 'Untitled competency'}</div>
                      {comp.description ? <div className="resume-summary-text">{comp.description}</div> : null}
                    </div>
                    {renderActions(comp, 'competencies')}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {canEdit ? (
          <div className="resume-add-row">
            <Button appearance="ghost" onClick={addCompetency}>
              <i className="bi bi-plus-lg me-2" />
              Add Competency
            </Button>
          </div>
        ) : null}
      </Panel>

      {/* Certifications */}
      <Panel bordered header={<span className="fw-semibold">Certifications</span>}>
        {state.certifications.length === 0 ? (
          <div className="text-muted">No certifications.</div>
        ) : (
          <div className="resume-grid">
            {state.certifications.map((cert) => (
              <div key={cert.key} className={`resume-entry ${cert.editing ? 'resume-entry--editing' : ''} ${cert.removing ? 'removing' : ''}`}>
                {cert.editing ? (
                  <>
                    <div className="resume-entry-grid">
                      <div className="resume-field resume-field--grow">
                        <label className="resume-label">Name</label>
                        <Input
                          value={cert.name}
                          placeholder="Certification name"
                          onChange={(v) => updateList('certifications', cert.key, { name: v })}
                        />
                      </div>
                      <div className="resume-field resume-field--grow">
                        <label className="resume-label">Governing Organization</label>
                        <Input
                          value={cert.governing_organization}
                          placeholder="Issuing organization"
                          onChange={(v) => updateList('certifications', cert.key, { governing_organization: v })}
                        />
                      </div>
                      <div className="resume-field">
                        <label className="resume-label">Start Date</label>
                        <DatePicker
                          oneTap
                          block
                          format="MM/yyyy"
                          value={parseDateOnlyLocal(cert.start_date)}
                          onChange={(d) => updateList('certifications', cert.key, { start_date: toDateOnly(d) })}
                        />
                      </div>
                      <div className="resume-field">
                        <label className="resume-label">End Date</label>
                        <DatePicker
                          oneTap
                          block
                          format="MM/yyyy"
                          value={parseDateOnlyLocal(cert.end_date)}
                          placeholder={cert.end_date ? undefined : 'No expiration'}
                          onChange={(d) => updateList('certifications', cert.key, { end_date: toDateOnly(d) })}
                        />
                      </div>
                    </div>
                    <div className="resume-entry-actions">
                      <Button
                        appearance="primary"
                        size="sm"
                        onClick={() => saveItem('certifications', cert.key)}
                        loading={savingKey === cert.key && saveResume.isPending}
                        disabled={!isItemDirty('certifications', cert) || saveResume.isPending}
                      >
                        <i className="bi bi-floppy me-2" />
                        Save
                      </Button>
                      <Button appearance="subtle" color="red" size="sm" onClick={() => requestDelete('certifications', cert)}>
                        <i className="bi bi-trash me-2" />
                        Remove
                      </Button>
                    </div>
                  </>
                ) : (
                  <div className="resume-summary">
                    <div className="resume-summary-main">
                      <div className="resume-summary-title">{cert.name || 'Untitled certification'}</div>
                      {cert.governing_organization ? (
                        <div className="resume-summary-text">{cert.governing_organization}</div>
                      ) : null}
                      <div className="resume-summary-sub">{formatRange(cert.start_date, cert.end_date, 'No expiration')}</div>
                    </div>
                    {renderActions(cert, 'certifications')}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {canEdit ? (
          <div className="resume-add-row">
            <Button appearance="ghost" onClick={addCertification}>
              <i className="bi bi-plus-lg me-2" />
              Add Certification
            </Button>
          </div>
        ) : null}
      </Panel>

      <ConfirmModal
        open={Boolean(pendingDelete)}
        title={`Remove ${pendingDelete?.noun}`}
        body={(
          <>
            Are you sure you want to remove{' '}
            {pendingDelete?.name ? <strong>{pendingDelete.name}</strong> : `this ${pendingDelete?.noun || 'item'}`}?
            {' '}This can&apos;t be undone.
          </>
        )}
        confirmText="Remove"
        danger
        backdrop="static"
        // Closes at once; the row animates its own removal behind it.
        onConfirm={() => { confirmDelete(); }}
        onCancel={cancelDelete}
      />
    </div>
  );
}
